/**
 * Tracking configuration utilities.
 * Handles page info retrieval and tracking config management.
 */

import { getPageInfo } from '../utils/page-meta'
import { CATEGORY_SECTION, TOP_NAV_LEVEL, TOP_NAV_DETAIL_THRESHOLD, CATEGORY_DETAIL_THRESHOLD } from '../constants/analytics'

/** Tracking configuration from page meta tags */
export interface TrackingPageConfig {
  pageSection: string
  pageStage: string
  pageName: string
  pageDept: string
}

/**
 * Gets the current page tracking configuration from meta tags.
 */
export const getTrackingConfig = (): TrackingPageConfig => {
  const pageInfo = getPageInfo()
  return {
    pageSection: pageInfo.pageSection ?? '',
    pageStage: pageInfo.pageStage ?? '',
    pageName: pageInfo.pageName ?? '',
    pageDept: pageInfo.pageDept ?? '',
  }
}

/**
 * Safely calls window.tracking.track with the given event and payload.
 * Handles the case where tracking is not available.
 *
 * @param eventName - The event name to track
 * @param payload - The event payload
 */
export const safeTrack = (eventName: string, payload: Record<string, unknown>): void => {
  if (window.tracking?.track) {
    window.tracking.track(eventName, payload)
  }
}

/**
 * Builds a navigation detail string from the navigation path, with optional deduplication.
 */
export const buildNavigationDetail = (navigationSection: string | undefined, navigationDetailPath: string[]): string => {
  const shouldDeduplicate =
    (navigationSection === TOP_NAV_LEVEL && navigationDetailPath.length > TOP_NAV_DETAIL_THRESHOLD) ||
    (navigationSection === CATEGORY_SECTION && navigationDetailPath.length > CATEGORY_DETAIL_THRESHOLD)

  if (shouldDeduplicate) {
    return Array.from(new Set(navigationDetailPath)).join('|')
  }

  return navigationDetailPath.join('|')
}

/**
 * Computes the destination path from a URL string.
 */
export const getDestinationPath = (href: string | undefined): string => {
  if (!href) return ''

  try {
    const url = new URL(href, window.location.origin)
    const currentHost = window.location.hostname
    // If the URL host matches current host, return just the pathname
    if (url.hostname === currentHost) {
      return url.pathname
    }
    // For external URLs, return host + pathname
    return url.host + url.pathname
  } catch {
    // If URL parsing fails, return the href as-is
    return href
  }
}
